import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Block as BlockIcon
} from '@mui/icons-material';

const WorkflowProgressIndicator = ({ currentStatus, entityType = 'sector' }) => {
  // Define workflow steps based on entity type
  const getWorkflowSteps = () => {
    if (entityType === 'trip') {
      return [
        { status: 'DRAFT', label: 'Draft', description: 'Charter created by Sales' },
        { status: 'PENDING_OPS', label: 'Pending Operations', description: 'Sent to Operations for processing' },
        { status: 'CREW_ASSIGNMENT', label: 'Crew Assignment', description: 'Aircraft and crew being assigned' },
        { status: 'READY_FOR_BA', label: 'Ready for BA', description: 'Awaiting breathalyzer test' },
        { status: 'READY_FOR_ATC', label: 'Ready for ATC', description: 'Awaiting ATC clearance' },
        { status: 'COMPLETED', label: 'Completed', description: 'Charter fully completed' }
      ];
    } else {
      // Sector workflow
      return [
        { status: 'PLANNED', label: 'Planned', description: 'Sector created' },
        { status: 'ASSIGNED', label: 'Assigned', description: 'Aircraft and crew assigned' },
        { status: 'CREW_ACCEPTED', label: 'Crew Accepted', description: 'Crew accepted assignment' },
        { status: 'READY_FOR_BA', label: 'Ready for BA', description: 'Awaiting breathalyzer test' },
        { status: 'BA_PASSED', label: 'BA Passed', description: 'Breathalyzer test completed' },
        { status: 'READY_FOR_ATC', label: 'Ready for ATC', description: 'Awaiting ATC clearance' },
        { status: 'CLEARED', label: 'Cleared', description: 'ATC clearance obtained' },
        { status: 'COMPLETED', label: 'Completed', description: 'Sector completed' }
      ];
    }
  };

  const steps = getWorkflowSteps();
  const currentStepIndex = steps.findIndex(step => step.status === currentStatus);
  const activeStep = currentStepIndex >= 0 ? currentStepIndex : 0;

  const getStepStatus = (stepIndex) => {
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'pending';
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'success';
    if (status === 'active') return 'primary';
    if (status === 'error') return 'error';
    return 'default';
  };

  // Check if status is rejected/cancelled
  const isRejected = ['CREW_REJECTED', 'BA_FAILED', 'ATC_FAILED', 'CANCELLED'].includes(currentStatus);
  const isError = isRejected;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
        Workflow Progress
      </Typography>
      <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 1 }}>
        {steps.map((step, index) => {
          const stepStatus = getStepStatus(index);
          const isCompleted = stepStatus === 'completed';
          const isActive = stepStatus === 'active';
          const isPending = stepStatus === 'pending';

          return (
            <Step key={step.status} completed={isCompleted} active={isActive}>
              <StepLabel
                error={isError && isActive}
                StepIconComponent={() => {
                  if (isCompleted) {
                    return <CheckCircleIcon sx={{ color: 'success.main' }} />;
                  } else if (isError && isActive) {
                    return <BlockIcon sx={{ color: 'error.main' }} />;
                  } else if (isActive) {
                    return <CheckCircleIcon sx={{ color: 'primary.main' }} />;
                  } else {
                    return <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />;
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: isActive ? 'bold' : 'normal' }}>
                    {step.label}
                  </Typography>
                  {isActive && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
      {isRejected && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="caption" color="error.main" sx={{ fontWeight: 'medium' }}>
            ⚠ Status: {currentStatus.replace(/_/g, ' ')} - Action Required
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WorkflowProgressIndicator;

